/** @jsx jsx */
import { jsx, ThemeUIStyleObject } from "theme-ui";
import React, { Fragment, PropsWithChildren } from "react";
import { TAXONOMIES_MAP } from "../../../carlsberg-headless/taxonomies-map";
import { THEMES_MAP } from "../../../carlsberg-headless/themes-map";
import {
  spacingDefaultStyles,
  spacingSmStyles,
  spacingMdStyles,
  spacingLgStyles,
  spacingWithBgColorDefaultStyles,
  spacingWithBgColorSmStyles,
  spacingWithBgColorLgStyles,
  spacingWithBgColorMdStyles,
  bgLighterGrayStyles,
  bgWhiteStyles,
  bgGreenStyles,
  bgRedStyles,
  spacingNoneStyles,
  bgRedToGrayStyles,
  bgGreenToGrayStyles,
  moduleStyles,
  whiteTopBorderSeperator,
} from "./styles";
import { ThemesField } from "../../../carlsberg-headless/types/fields/Themes";
import { TaxonomiesField } from "../../../carlsberg-headless/types/fields/Taxonomies";
import { PrimitiveProps } from "@styles/types";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { variantFadeIn } from "@shared-components/LayoutContainer/animations";
import { Box } from "@shared-components/Box/Box";
import ThreeLeafCloverSvg from "@svg-icons/three-leaf-clover.component.svg";

export interface LayoutContainerProps extends PrimitiveProps {
  themes?: ThemesField;
  taxonomies?: TaxonomiesField;
  disableAnimate?: boolean;
}

const ContentBox: React.FC<PropsWithChildren<LayoutContainerProps>> = ({
  themes,
  children,
}) => {
  if (themes?.includes(THEMES_MAP.layout_bg.layout_bg_dark_red_to_light_gray)) {
    return (
      <Box
        sx={{
          position: "relative",
          zIndex: "1",
        }}
      >
        {children}
      </Box>
    );
  } else {
    return <Fragment>{children}</Fragment>;
  }
};

const hasBgColor = (themes?: string[]) => {
  if (!themes || themes.length === 0) return true;

  return (
    themes.includes(THEMES_MAP.layout_bg.layout_bg_lightergray) ||
    themes.includes(THEMES_MAP.layout_bg.layout_bg_white) ||
    themes.includes(THEMES_MAP.layout_bg.layout_bg_red) ||
    themes.includes(THEMES_MAP.layout_bg.layout_bg_green) ||
    themes.includes(THEMES_MAP.layout_bg.layout_bg_dark_red_to_light_gray) ||
    themes.includes(THEMES_MAP.layout_bg.layout_bg_dark_green_to_light_gray)
  );
};

export const getBgColorStyles = (themes?: string[]): ThemeUIStyleObject => {
  let styles: ThemeUIStyleObject;
  if (!themes || themes.length === 0 || !hasBgColor(themes)) {
    styles = {};
  } else {
    switch (true) {
      case themes.includes(THEMES_MAP.layout_bg.layout_bg_lightergray):
        styles = bgLighterGrayStyles;
        break;

      case themes.includes(THEMES_MAP.layout_bg.layout_bg_white):
        styles = bgWhiteStyles;
        break;

      case themes.includes(THEMES_MAP.layout_bg.layout_bg_red):
        styles = bgRedStyles;
        break;

      case themes.includes(THEMES_MAP.layout_bg.layout_bg_green):
        styles = bgGreenStyles;
        break;

      case themes.includes(
        THEMES_MAP.layout_bg.layout_bg_dark_red_to_light_gray
      ):
        styles = bgRedToGrayStyles;
        break;

      case themes.includes(
        THEMES_MAP.layout_bg.layout_bg_dark_green_to_light_gray
      ):
        styles = bgGreenToGrayStyles;
        break;
      default:
        styles = {};
    }
  }

  return styles;
};

const getSpacingStyles = (taxonomies?: string[], hasBg?: boolean) => {
  let styles: ThemeUIStyleObject;

  if (!taxonomies || taxonomies.length === 0)
    return hasBg ? spacingWithBgColorDefaultStyles : spacingDefaultStyles;

  switch (true) {
    case taxonomies.includes(TAXONOMIES_MAP.layout.layout_spacing_none):
      styles = spacingNoneStyles;
      break;
    case taxonomies.includes(TAXONOMIES_MAP.layout.layout_spacing_sm):
      styles = hasBg ? spacingWithBgColorSmStyles : spacingSmStyles;
      break;
    case taxonomies.includes(TAXONOMIES_MAP.layout.layout_spacing_md):
      styles = hasBg ? spacingWithBgColorMdStyles : spacingMdStyles;
      break;
    case taxonomies.includes(TAXONOMIES_MAP.layout.layout_spacing_lg):
      styles = hasBg ? spacingWithBgColorLgStyles : spacingLgStyles;
      break;
    default:
      styles = hasBg ? spacingWithBgColorDefaultStyles : spacingDefaultStyles;
  }

  return styles;
};

const getBorderTop = (taxonomies?: string[]) => {
  return taxonomies?.includes(TAXONOMIES_MAP.layout.layout_white_top_separator)
    ? whiteTopBorderSeperator
    : "";
};

export const hopLeaf = (taxonomies: string[], spacingStyles: any) => {
  // Map of taxonomy names to their corresponding colors
  const hopLeafColors = {
    [TAXONOMIES_MAP.general.green_hop_leaf]: "#284A41", // Green hop leaf color
    [TAXONOMIES_MAP.general.red_hop_leaf]: "#561415", // Red hop leaf color
  };

  // Find the first matching taxonomy in the input array that exists in the hopLeafColors object
  const matchingTaxonomy = taxonomies?.find((taxonomy) =>
    hopLeafColors.hasOwnProperty(taxonomy)
  );

  // Get the corresponding color based on the matching taxonomy, or use null if no matching taxonomy is found
  const fillColor = matchingTaxonomy ? hopLeafColors[matchingTaxonomy] : null;

  const topSpacing = spacingStyles["my"] || spacingStyles["py"];

  // If fillColor is not null, return the ThreeLeafCloverSvg component with the specified styles and fillColor
  return fillColor ? (
    <Box
      sx={{
        mt: topSpacing,
        mx: "auto",
        width: 23,
        height: 22,
      }}
    >
      <ThreeLeafCloverSvg width={23} height={22} fill={fillColor} />
    </Box>
  ) : null; // If fillColor is null (no matching taxonomy found), return null (no rendering)
};

const LayoutContainer: React.FC<PropsWithChildren<LayoutContainerProps>> = ({
  id,
  children,
  themes,
  taxonomies,
  sx,
  disableAnimate,
}) => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  if (disableAnimate) {
    return (
      <div
        id={id}
        sx={{
          ...sx,
          ...getSpacingStyles(taxonomies, hasBgColor(themes)),
          ...getBgColorStyles(themes),
          ...getBorderTop(taxonomies),
        }}
      >
        <div sx={moduleStyles}>{children}</div>
      </div>
    );
  }

  if (hasBgColor(themes)) {
    return (
      <Box id={id} sx={{ ...getBgColorStyles(themes) }}>
        <motion.div
          ref={ref}
          initial={"hidden"}
          animate={inView ? "visible" : "hidden"}
          variants={variantFadeIn}
          sx={{
            ...sx,
            ...getSpacingStyles(taxonomies, hasBgColor(themes)),
            ...getBorderTop(taxonomies),
          }}
        >
          <ContentBox themes={themes}>
            {children}
            {hopLeaf(
              taxonomies,
              getSpacingStyles(taxonomies, hasBgColor(themes))
            )}
          </ContentBox>
        </motion.div>
      </Box>
    );
  }

  return (
    <motion.div
      ref={ref}
      initial={"hidden"}
      animate={inView ? "visible" : "hidden"}
      variants={variantFadeIn}
      sx={{
        ...sx,
        ...getSpacingStyles(taxonomies, hasBgColor(themes)),
        ...getBgColorStyles(taxonomies),
      }}
    >
      <ContentBox themes={themes}>
        {children}
        {hopLeaf(taxonomies, getSpacingStyles(taxonomies, hasBgColor(themes)))}
      </ContentBox>
    </motion.div>
  );
};

export { LayoutContainer };
